import React from 'react';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';
import { alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

function getMonthOptions() {
  return [
    { value: '2026-01', label: 'Ocak 2026' },
    { value: '2026-02', label: 'Şubat 2026' },
    { value: '2026-03', label: 'Mart 2026' },
    { value: '2026-04', label: 'Nisan 2026' },
    { value: '2026-05', label: 'Mayıs 2026' },
    { value: '2026-06', label: 'Haziran 2026' },
    { value: '2026-07', label: 'Temmuz 2026' },
    { value: '2026-08', label: 'Ağustos 2026' },
    { value: '2026-09', label: 'Eylül 2026' },
    { value: '2026-10', label: 'Ekim 2026' },
    { value: '2026-11', label: 'Kasım 2026' },
    { value: '2026-12', label: 'Aralık 2026' },
  ];
}

const fieldSx = {
  '& .MuiInputBase-root': {
    height: 42,
    fontSize: 13,
    borderRadius: '12px',
    backgroundColor: '#fff',
  },
  '& .MuiInputLabel-root': {
    fontSize: 12,
  },
};

const switchBoxSx = {
  minHeight: 42,
  px: 1,
  borderRadius: '12px',
  border: '1px solid rgba(15,23,42,0.12)',
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#fff',
};

const switchLabelSx = {
  m: 0,
  width: '100%',
  '& .MuiFormControlLabel-label': {
    fontSize: 12,
    lineHeight: 1.2,
  },
};

function SwitchField({ checked, onChange, label }) {
  return (
    <MDBox sx={switchBoxSx}>
      <FormControlLabel
        sx={switchLabelSx}
        control={<Switch size="small" checked={checked} onChange={onChange} />}
        label={label}
      />
    </MDBox>
  );
}

export default function PayrollSettingsFormMini({
  value,
  onChange,
  onOpenPricingModal,
  lessonRuleCount,
  packageRuleCount,
}) {
  const monthOptions = React.useMemo(() => getMonthOptions(), []);
  const payrollType = value?.payrollType || 'HOURLY';

  const isHourly = payrollType === 'HOURLY';
  const isMonthly = payrollType === 'MONTHLY';
  const isPercentage = payrollType === 'PERCENTAGE';

  return (
    <MDBox sx={{ display: 'grid', gap: 1.1 }}>
      <Grid container spacing={1.1}>
        <Grid item xs={12} md={3}>
          <TextField
            select
            fullWidth
            size="small"
            label="Dönem"
            value={value?.period || ''}
            onChange={(e) => onChange?.({ period: e.target.value })}
            sx={fieldSx}
          >
            {monthOptions.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            select
            fullWidth
            size="small"
            label="Hesaplama şekli"
            value={payrollType}
            onChange={(e) => onChange?.({ payrollType: e.target.value })}
            sx={fieldSx}
          >
            <MenuItem value="HOURLY">Saatlik</MenuItem>
            <MenuItem value="MONTHLY">Tam zamanlı</MenuItem>
            <MenuItem value="PERCENTAGE">Yüzdelik</MenuItem>
          </TextField>
        </Grid>

        {isMonthly ? (
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Aylık maaş"
              type="number"
              value={value?.monthlySalary || ''}
              onChange={(e) => onChange?.({ monthlySalary: e.target.value })}
              sx={fieldSx}
              InputProps={{
                endAdornment: <InputAdornment position="end">₺</InputAdornment>,
              }}
            />
          </Grid>
        ) : null}

        {isPercentage ? (
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Yüzde oranı"
              type="number"
              value={value?.percentageRate || ''}
              onChange={(e) => onChange?.({ percentageRate: e.target.value })}
              sx={fieldSx}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </Grid>
        ) : null}

        {isHourly ? (
          <Grid item xs={12} md={9}>
            <MDBox
              sx={{
                minHeight: 42,
                px: 1.1,
                py: 0.9,
                borderRadius: '16px',
                border: '1px solid rgba(15,23,42,0.10)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
                background:
                  'linear-gradient(135deg, rgba(14,165,233,0.08), rgba(255,255,255,0.98))',
              }}
            >
              <MDBox>
                <MDTypography variant="caption" sx={{ opacity: 0.72, display: 'block' }}>
                  Kural tipi
                </MDTypography>
                <MDTypography
                  variant="caption"
                  sx={{ color: 'text.secondary', display: 'block', mb: 0.6 }}
                >
                  Önce seansın kişi sayısına göre ücret uygulanır. Paket için özel fiyat tanımlıysa
                  bu tutar seans ücretini ezer.
                </MDTypography>
                <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.55, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    label={`${lessonRuleCount || 0} seans tipi`}
                    sx={{ borderRadius: '999px', fontWeight: 800 }}
                  />
                  <Chip
                    size="small"
                    label={`${packageRuleCount || 0} paket override`}
                    sx={{ borderRadius: '999px', fontWeight: 800 }}
                  />
                </MDBox>
              </MDBox>

              <Button
                variant="contained"
                startIcon={<Icon fontSize="small">auto_awesome</Icon>}
                onClick={onOpenPricingModal}
                sx={{
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontWeight: 900,
                  minHeight: 38,
                  px: 1.8,
                  color: '#fff !important',
                  background: 'linear-gradient(135deg, #0f172a, #0369a1)',
                  boxShadow: '0 10px 20px rgba(3,105,161,0.18)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #111827, #0284c7)',
                    boxShadow: '0 14px 24px rgba(3,105,161,0.24)',
                  },
                }}
              >
                Ücretleri düzenle
              </Button>
            </MDBox>
          </Grid>
        ) : null}
      </Grid>

      <Grid container spacing={1.1}>
        {isHourly ? (
          <Grid item xs={12} md={3}>
            <SwitchField
              checked={Boolean(value?.showUntakenAttendanceSessions)}
              onChange={(e) => onChange?.({ showUntakenAttendanceSessions: e.target.checked })}
              label="Yoklaması girilmeyenleri göster"
            />
          </Grid>
        ) : null}

        {isHourly ? (
          <Grid item xs={12} md={3}>
            <SwitchField
              checked={Boolean(value?.includeMissedSessions)}
              onChange={(e) => onChange?.({ includeMissedSessions: e.target.checked })}
              label="Katılım olmayanları göster"
            />
          </Grid>
        ) : null}

        {!isMonthly ? (
          <Grid item xs={12} md={3}>
            <SwitchField
              checked={Boolean(value?.applyCardCommission)}
              onChange={(e) => onChange?.({ applyCardCommission: e.target.checked })}
              label="Kart kesintisi uygula"
            />
          </Grid>
        ) : null}

        {!isMonthly && value?.applyCardCommission ? (
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Kesinti oranı"
              type="number"
              value={value?.cardCommissionPercent || ''}
              onChange={(e) => onChange?.({ cardCommissionPercent: e.target.value })}
              sx={{
                ...fieldSx,
                '& .MuiInputBase-root': {
                  ...fieldSx['& .MuiInputBase-root'],
                  boxShadow: `0 0 0 3px ${alpha('#0284c7', 0.08)}`,
                },
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </Grid>
        ) : null}
      </Grid>
    </MDBox>
  );
}
